import { defineStore } from 'pinia';
import { IGetBeophoeResponse } from '@/types/suheng';
import {
  handleEditMeditationClickParams,
  IGetWeeklyMeditationsResponse,
  IMeditationRoom,
  IGetMeditationRoomListResponse,
} from '@/types/meditation';
import { boardReplyType } from '@/types/volunteerTypes';
import { Ref } from 'vue';

export const MODAL_TYPES = {
  /* 수행법회페이지 모달 */
  MIND_SHARE: 'mind-share',
  MIND_SHARE_DELETE: 'mind-share-delete',
  MIND_SHARE_RESET: 'mind-share-reset',
  DHARMA_EDIT: 'dharma-edit',

  /* 명상페이지 모달 */
  MEDITATION_EDIT: 'meditation-edit',
  /* 방 선택하기, 유저&어드민 같이 사용 */
  MIND_SHARE_CHOICE_ROOM: 'mind-share-choice-room',
  /* 방 선택하기, 어드민 전용, 중첩모달 */
  NESTED_MINDSHARE_EDIT: 'nested-mindshare-edit',
  VOLUNTEER_CONFIRM: 'volunteer-confirm',
  VOLUNTEER_NOTICE: 'volunteer-notice',
  QNA_EDIT: 'qna-edit',
  QNA_DELETE: 'qna-delete',
  INVITE_DELETE: 'invite-delete',
  IMAGE_VIEWER: 'image-viewer',
  SIMPLE_ALERT: 'simple-alert',
  LOGIN_HELP: 'login-help',
  POLICY_READ: 'policy-read',
  RETURN_APPLICATION: 'return-application',
} as const;

type ModalType = (typeof MODAL_TYPES)[keyof typeof MODAL_TYPES];

interface BaseModalProps {
  nestedOnConfirm?: (selectedRoomItem: IMeditationRoom) => void;
}

interface MindShareModalProps extends BaseModalProps {
  mode: 'create' | 'edit';
  initialContent?: string;
  mindShareId?: number;
  onSubmit: (content: string, mode: 'create' | 'edit', id?: number) => void;
}

interface MindShareDeleteModalProps extends BaseModalProps {
  id: number;
  memId: number;
  onConfirm: (id: number, memId: number) => void;
}

export interface MindShareResetModalProps extends BaseModalProps {
  onConfirm: (memId: number) => void;
  memId: number;
}

export interface IMindShareChoiceRoomModalProps {
  nestedOnConfirm: (mode: 'edit' | 'create', selectedRoomItem: Partial<IMeditationRoom>) => Promise<void>;
  onConfirm: (roomId: number) => void;
  meditationRoomList: Ref<IGetMeditationRoomListResponse['dharmaMeet']['getMeditationRoomList'] | null>;
  selectedRoomItem: Ref<IMeditationRoom | null>;
  deleteOnConfirm: (id: number) => void;
  type: 'user' | 'admin';
}

export interface DharmaEditModalProps extends BaseModalProps {
  beophoeItem: IGetBeophoeResponse['dharmaMeet']['getLatestDharma'] | null;
  memId: number;
  onSubmit: (id: number, date: string, linkUrl: string, memId: number, title: string) => void;
}

export interface MeditationEditModalProps extends BaseModalProps {
  mode: 'create' | 'edit';
  weeklyMeditations: IGetWeeklyMeditationsResponse['dharmaMeet']['getWeeklyMeditations'] | null;
  handleEditMeditationClick: (params: handleEditMeditationClickParams) => void;
}

export interface NestedMindshareEditModalProps extends BaseModalProps {}

export interface VolunteerConfirmModalProps extends BaseModalProps {
  onConfirm: (memId: number) => void;
  isSubmitting: boolean;
  memId: number;
}

export interface VolunteerNoticeModalProps extends BaseModalProps {
  onConfirm: (memId: number) => void;
  isSubmitting: boolean;
  memId: number;
  isReapply?: boolean;
}

export interface QnaEditModalSubmitPayload {
  boardId: number;
  majorCat: string;
  subCat?: string;
  message: string;
  file?: File | null;
}

export interface QnaEditModalProps extends BaseModalProps {
  item: boardReplyType;
  categories: { text: string; value: string }[];
  inquiryTypesMap: Record<string, { text: string; value: string }[]>;
  onSubmit: (payload: QnaEditModalSubmitPayload) => Promise<{ success: boolean; error?: string }>;
}

export interface QnaDeleteModalProps extends BaseModalProps {
  boardId: number;
  majorCat: string;
  subCat?: string;
  onConfirm: () => Promise<{ success: boolean; error?: string }>;
}

export interface InviteDeleteModalProps extends BaseModalProps {
  guestId: number;
  onConfirm: (guestId: number) => Promise<void>;
}

export interface ImageViewerModalProps extends BaseModalProps {
  src: string;
  filename?: string;
  alt?: string;
}

export interface SimpleAlertModalProps extends BaseModalProps {
  message: string;
  title?: string;
  confirmText?: string;
  messageClass?: string;
}

export interface LoginHelpModalProps extends BaseModalProps {}

export interface PolicyReadModalProps extends BaseModalProps {
  title: string;
  heading: string;
  description: string;
  sections: { title?: string; start: number; items: string[] }[];
  notes?: string[];
  onConfirm: () => void;
}

export interface ReturnApplicationModalProps extends BaseModalProps {
  absenceId: number;
  expectedReturnDate?: string;
  onConfirm: (payload: { absenceId: number }) => Promise<void>;
}

export type ModalProps =
  | MindShareModalProps
  | MindShareDeleteModalProps
  | MindShareResetModalProps
  | IMindShareChoiceRoomModalProps
  | DharmaEditModalProps
  | MeditationEditModalProps
  | NestedMindshareEditModalProps
  | VolunteerConfirmModalProps
  | VolunteerNoticeModalProps
  | QnaEditModalProps
  | QnaDeleteModalProps
  | InviteDeleteModalProps
  | ImageViewerModalProps
  | SimpleAlertModalProps
  | LoginHelpModalProps
  | PolicyReadModalProps
  | ReturnApplicationModalProps;

// 모달 타입에 따른 props 매핑
interface ModalPropsMap {
  [MODAL_TYPES.MIND_SHARE]: MindShareModalProps;
  [MODAL_TYPES.MIND_SHARE_DELETE]: MindShareDeleteModalProps;
  [MODAL_TYPES.MIND_SHARE_RESET]: MindShareResetModalProps;
  [MODAL_TYPES.MIND_SHARE_CHOICE_ROOM]: IMindShareChoiceRoomModalProps;
  [MODAL_TYPES.DHARMA_EDIT]: DharmaEditModalProps;
  [MODAL_TYPES.MEDITATION_EDIT]: MeditationEditModalProps;
  [MODAL_TYPES.NESTED_MINDSHARE_EDIT]: NestedMindshareEditModalProps;
  [MODAL_TYPES.VOLUNTEER_CONFIRM]: VolunteerConfirmModalProps;
  [MODAL_TYPES.VOLUNTEER_NOTICE]: VolunteerNoticeModalProps;
  [MODAL_TYPES.QNA_EDIT]: QnaEditModalProps;
  [MODAL_TYPES.QNA_DELETE]: QnaDeleteModalProps;
  [MODAL_TYPES.INVITE_DELETE]: InviteDeleteModalProps;
  [MODAL_TYPES.IMAGE_VIEWER]: ImageViewerModalProps;
  [MODAL_TYPES.SIMPLE_ALERT]: SimpleAlertModalProps;
  [MODAL_TYPES.LOGIN_HELP]: LoginHelpModalProps;
  [MODAL_TYPES.POLICY_READ]: PolicyReadModalProps;
  [MODAL_TYPES.RETURN_APPLICATION]: ReturnApplicationModalProps;
}

interface ModalState {
  isOpen: boolean;
  type: ModalType | null;
  props: Record<string, unknown> | null;
}

export const useModalStore = defineStore('modal', {
  state: (): ModalState => ({
    isOpen: false,
    type: null,
    props: null,
  }),

  actions: {
    open<T extends ModalType>(type: T, props?: ModalPropsMap[T]) {
      this.type = type;
      this.props = (props as Record<string, unknown>) || null;
      this.isOpen = true;
    },

    close(type?: ModalType) {
      if (!type || this.type === type) {
        this.isOpen = false;
        this.type = null;
        this.props = null;
      }
    },
  },
});
